import { flattenObject, getArgByType } from '../helpers/common';
import { commentNode } from '../helpers/vue';

export default {
    install(Vue, options) {
        if(!('i18n' in options)) {
            throw new Error('Cannot install translation plugin: Missing i18n plugin instance in options');
        }

        // Install vue-i18n plugin
        Vue.prototype._i18n = options.i18n;
        Vue.use(options.i18n);

        /**
         * Get caller arguments for vue i18n translation if the requested translation exists.
         * Returns an Object with keys 'method' and 'args' for calling the actual vue i18n translation method.
         * Returns false if there is no translation for the given path and args.
         * 
         * Also respects following properties (prop/data/computed) which may be set on the calling vue component instance:
         * - translationReplacements: Additional replacements for the translation value
         * - translationKeyReplacements: Additional replacements for the trnslation key path
         * - translationsKeyPath: Base key path - will be prefixed to the given key path.
         * - translationsKeyPaths: Additional base key paths, will be used as prefix for the given key path. The first path which exists in the translations will be used.
         * - translations: Additional translations in which it should search for the given key path(s).
         * 
         * @param {String} keyPath The key path to search for.
         * @param {(Object|Array)} replacements Replacements for the found translation value.
         * @param {Array} keyReplacements Replacements for the key path.
         * @param {Number} count Count for pluralization.
         */
        Vue.prototype.__a = function(keyPath, replacements = null, keyReplacements = null, count = null) {
            if(typeof replacements == 'number') {
                count = replacements;
                replacements = null;
            } else if(typeof keyReplacements == 'number') {
                count = keyReplacements;
                keyReplacements = null;
            }

            const needsKeyReplacements = keyPath.indexOf('?') != -1;

            if(needsKeyReplacements && !keyReplacements && 'translationKeyReplacements' in this && this.translationKeyReplacements) {
                keyReplacements = this.translationKeyReplacements;
            }
            if(needsKeyReplacements && !keyReplacements && replacements) {
                keyReplacements = replacements;
                replacements = null;
            }
            if(needsKeyReplacements && keyReplacements) {
                for(const r of keyReplacements) {
                    keyPath = keyPath.replace('?', r);
                }
            }

            if('translationReplacements' in this && this.translationReplacements) {
                replacements = { ...this.translationReplacements, ...(replacements || {}) };
            }

            let ignoreTranslationKeyPaths = false;

            if(keyPath[0] == '#') {
                ignoreTranslationKeyPaths = true;
                keyPath = keyPath.substr(1);
            }

            const keyPathDefault = 'translationsKeyPath' in this && this.translationsKeyPath && !ignoreTranslationKeyPaths 
                ? this.translationsKeyPath + '.' + keyPath 
                : keyPath;
            const translationsDefault = options.i18n._getMessages();
            const locale = options.i18n.locale;
            const pluralization = count !== null && typeof count == 'number' ? true : false;
            const i18nCallArgs = {
                'method': pluralization ? 'tc' : 't',
                'args': [
                    null,
                    locale,
                    null,
                    this
                ]
            };

            if(pluralization) {
                i18nCallArgs.args.push(count);
            }

            i18nCallArgs.args.push(replacements ? flattenObject(replacements) : null);

            let tryPaths = [ keyPathDefault ];
            let tryTranslations = [ translationsDefault[locale] ];

            if('translationsKeyPaths' in this && this.translationsKeyPaths && !ignoreTranslationKeyPaths) {
                for(const basePath of this.translationsKeyPaths) {
                    tryPaths.push(basePath + '.' + keyPath);
                }
            }

            if('translations' in this && this.translations) {
                tryTranslations = tryTranslations.concat(Array.isArray(this.translations) ? this.translations : [ this.translations ]);
            }

            for(const t of tryTranslations) {
                for(const p of tryPaths) {
                    if(options.i18n._exist(t, p)) {
                        i18nCallArgs.args[0] = p;
                        i18nCallArgs.args[2] = {
                            [locale]: t
                        };
                    }
                }
            }

            return i18nCallArgs.args[0] ? i18nCallArgs : null;
        }
        
        /**
         * Main instance method for getting translated output.
         * 
         * @param {String} keyPath 
         * @param {(Object|Array)} replacements 
         * @param {Object} keyReplacements 
         * @param {Number} count 
         * @param {String} alt
         */
        Vue.prototype.__ = function(keyPath, replacements = null, keyReplacements = null, count = null, alt = null) {
            alt = getArgByType(alt, 'string', [ replacements, keyReplacements, count, keyPath ]);
            count = getArgByType(count, 'number', [ replacements, keyReplacements ]);
            keyReplacements = getArgByType(keyReplacements, 'object');
            replacements = getArgByType(replacements, 'object');

            const i18nCallArgs = this.__a(keyPath, replacements, keyReplacements, count);

            return i18nCallArgs ? options.i18n['_' + i18nCallArgs.method].apply(options.i18n, i18nCallArgs.args) : alt;
        };

        /**
         * Directive for rendering an element with a translation as the inner text only if there is a translation for the given key.
         * Allows the same args as the main translation method __.
         */
        Vue.directive('tif', function(el, binding, vnode) {
            const vm = vnode.context;
            let keyPath = binding.value;
            let args = null;
            let keyArgs = null;
            let count = null;
            let alt = null;
            
            if(typeof binding.value == 'object') {
                if('path' in binding.value) {
                    keyPath = binding.value.path;
                }
                if('args' in binding.value) {
                    args = flattenObject(binding.value.args);
                }
                if('keyArgs' in binding.value) {
                    keyArgs = binding.value.keyArgs;
                }
                if('count' in binding.value) {
                    count = binding.value.count;
                }
                if('alt' in binding.value) {
                    alt = binding.value.alt;
                }
            }

            const i18nCallArgs = vm.__a(keyPath, args, keyArgs, count);

            if(i18nCallArgs) {
                el._vt = el.innerHTML = options.i18n['_' + i18nCallArgs.method].apply(options.i18n, i18nCallArgs.args);
            } else {
                if(alt !== null) {
                    el.innerHTML = alt;
                } else {
                    commentNode(el, vnode);
                }
            }
        });
    }
};